/**
 * Registers the 'sidebar' directive with the given module.
 * This directive is used to create a sidebar element with specific behavior and template.
 * @param {object} module - The AngularJS module to which the directive will be registered.
 */
export function registerSidebarDirective(module) {
    module.directive('sidebar', function () {
        return {
            restrict: 'E', // Restricts the directive to element usage only.
            replace: true, // Replaces the element with the template.
            scope: {
                task: '=' // Two-way binding for the task property.
            },
            templateUrl: '/views/ib/modules/sidebar/Sidebar.html', // URL of the template to be used.
            controller: 'SidebarController' // Controller associated with the directive.
        };
    });
}

/**
 * Registers the 'sidebarHeight' directive with the given module.
 * This directive adjusts the height of the sidebar based on various conditions and interactions.
 * @param {object} module - The AngularJS module to which the directive will be registered.
 */
export function registerSidebarHeightDirective(module) {
    module.directive('sidebarHeight', ['$', '$window', function ($, $window) {
        return {
            restrict: 'A', // Restricts the directive to attribute usage only.
            link: function (scope, element, attrs) {

                let elementStyles = $window.getComputedStyle(element[0]).paddingBottom || 0,
                    allowedTolerance = 4, // in pixels added to wrapper's height, so that the question list is considered a being full listed
                    sidebarWrapperQuestionBorder = 0; //parseInt($('.sidebar-wrapper .panel-heading.question-group').css('border-top'));
                let lastVisibleUp = 0;
                let nextVisibleDown = 0;

                /**
                 * Determines the visible height of an element within the question list.
                 * @param {jQuery} elem - The element to check.
                 * @returns {number} - The visible height of the element.
                 */
                const visibleInQuestionList = function (elem) {
                    let elemH = elem.outerHeight(),
                        q = element.find('.sidebar-questionlist-wrapper')[0].getBoundingClientRect(),
                        qTop = q.top,
                        qBottom = q.bottom,
                        e = elem[0].getBoundingClientRect(),
                        eTop = e.top,
                        eBottom = e.bottom;

                    if (eTop > qTop) {
                        return Math.max(0, eBottom < qBottom ? elemH : qBottom - eTop);
                    } else if (eTop < qTop) {
                        return Math.max(0, eBottom >= qTop ? eBottom - qTop : elemH - qTop);
                    }
                    return elemH;
                };

                /**
                 * Calculates the total height of all visible questions.
                 * @returns {number} - The total height of all visible questions.
                 */
                const getAllQuestionsHeight = function () {
                    let questions = getAvailableSheets(), total = 0;
                    for (let i = 0; i < questions.length; i++) {
                        total += visibleInQuestionList($(questions[i]));
                    }
                    return total;
                };

                /**
                 * Checks if all questions are visible within the allowed tolerance.
                 * @returns {boolean} - True if all questions are visible, false otherwise.
                 */
                const areAllQuestionsVisible = function () {
                    return getAllQuestionsHeight() <= getQuestionWrapperHeight() - allowedTolerance ? true : false;
                };

                /**
                 * Retrieves all available question sheets.
                 * @returns {Array} - An array of question sheets.
                 */
                const getAvailableSheets = function () {
                    return element.find('.sidebar-questionlist').find('div[id^="sheet-item-"]');
                };

                /**
                 * Retrieves the question group for a given sheet.
                 * @param {jQuery} sheet - The sheet element.
                 * @returns {jQuery} - The question group element.
                 */
                const getQuestionGroup = function (sheet) {
                    return $(sheet).parent().find('div[id="question-group-' + $(sheet).attr('data-uuid') + '"]') || [];
                };

                /**
                 * Retrieves the coordinates of the first and last question.
                 * @returns {Object} - An object containing the top and bottom coordinates of the first and last question.
                 */
                const getFirstAndLastQuestionCoords = function () {
                    let availableSheets = getAvailableSheets();

                    return {
                        'first': {
                            'top': availableSheets.get(0).getBoundingClientRect().top,
                            'bottom': availableSheets.get(0).getBoundingClientRect().bottom
                        },
                        'last': {
                            'top': availableSheets.get(availableSheets.length - 1).getBoundingClientRect().top,
                            'bottom': availableSheets.get(availableSheets.length - 1).getBoundingClientRect().bottom
                        }
                    }
                };

                /**
                 * Retrieves the height of the question wrapper.
                 * @returns {number} - The height of the question wrapper.
                 */
                const getQuestionWrapperHeight = function () {
                    return parseInt(element.find('.sidebar-questionlist-wrapper').height());
                };

                /**
                 * Retrieves the height of the question list wrapper.
                 * @returns {number} - The height of the question list wrapper.
                 */
                const getQuestionListWrapperHeight = function () {
                    let value = parseInt(element.find('.sidebar-questionlist .panel-group').outerHeight() || element.find('.sidebar-questionlist .panel-group').height()),
                        paddingTop = parseInt(element.find('.sidebar-wrapper').css('padding-top')) || 0,
                        paddingBottom = parseInt(element.find('.sidebar-wrapper').css('padding-bottom')) || 0;

                    return value ? value : getAllQuestionsHeight() + paddingTop + paddingBottom
                };

                /**
                 * Shows or hides the scroll buttons based on the height conditions.
                 */
                const showButtons = function () {

                    if (getQuestionListWrapperHeight() + sidebarWrapperQuestionBorder > getQuestionWrapperHeight() + allowedTolerance) {
                        scope.scrollUpButton = true;
                        scope.scrollDownButton = true;

                    } else {
                        scope.scrollUpButton = false;
                        scope.scrollDownButton = false;
                    }

                    // is there a pre-release button?
                    if (element.find('.background-btn').length > 0) {
                        let preReleaseButtonHeight = element.find('.background-btn').height(),
                            buttonOnTop = 20 + 2;  // height + borders. It looks as useless now. TODO: ask B. about this

                        if (preReleaseButtonHeight < element.find('.background-btn').outerHeight()) {
                            preReleaseButtonHeight = element.find('.background-btn').outerHeight()
                        }

                        element.css({
                            'padding-bottom': preReleaseButtonHeight + parseInt(elementStyles)
                        })
                    }
                };

                //used in onScroll functions
                let scrolled = 0;

                /**
                 * Handles the scroll up button click event.
                 */
                scope.onScrollUpButton = function () {
                    let availableSheets = getAvailableSheets();

                    scrollup:
                        for (let i = 1; i < availableSheets.length; i++) {

                            let thisQuestion = availableSheets[i],
                                tqTop = thisQuestion.getBoundingClientRect().top,
                                tqHeight = thisQuestion.getBoundingClientRect().height,
                                previousQuestion = availableSheets[i - 1],
                                pqTop = previousQuestion.getBoundingClientRect().top,
                                pqHeight = previousQuestion.getBoundingClientRect().height,
                                qList = element.find('.sidebar-questionlist-wrapper'),
                                qWrapperBottom = qList.get(0).getBoundingClientRect().bottom;

                            if (visibleInQuestionList($(availableSheets[i])) > 0) {
                                scrolled = $(previousQuestion).position().top;
                                qList.animate({scrollTop: scrolled}, 'slow');
                                break scrollup;
                            }
                            if (i === availableSheets.length - 1) {
                                qList.animate({scrollTop: lastVisibleUp}, 'slow');
                                break scrollup;
                            }
                        }
                };

                /**
                 * Handles the scroll down button click event.
                 */
                scope.onScrollDownButton = function () {

                    let availableSheets = getAvailableSheets(),
                        qList = element.find('.sidebar-questionlist-wrapper'),
                        qWrapperBottom = qList.get(0).getBoundingClientRect().bottom,
                        lastQuestion = availableSheets[availableSheets.length - 1],
                        lastQuestionGroup = getQuestionGroup(lastQuestion),
                        arrowSelector = 'span.menu-toggle-arrow',
                        collapseTrigger = 'in',
                        arrowOpenClass = 'open';

                    if (visibleInQuestionList($(lastQuestion)) && !$(lastQuestion).find(arrowSelector).hasClass(arrowOpenClass)) {

                        if (lastQuestionGroup.length) {
                            // opens the question group
                            $(lastQuestion).find(arrowSelector).toggleClass(arrowOpenClass);
                            lastQuestionGroup.toggleClass(collapseTrigger);

                            scrolled = scrolled + ($(lastQuestion).get(0).getBoundingClientRect().height + lastQuestionGroup.get(0).getBoundingClientRect().height);
                            qList.animate({scrollTop: scrolled}, 'slow');
                        }
                    }

                    for (let z = 1; z < availableSheets.length; z++) {
                        if (visibleInQuestionList($(availableSheets[z])) > 0) {
                            lastVisibleUp = $($(availableSheets[z])).position().top;
                            // nextVisibleDown = $($(availableSheets[z + 1])).position().top;
                        }
                    }

                    scrolldown:
                        for (let i = availableSheets.length - 2; i >= 0; i--) {
                            if (visibleInQuestionList($(availableSheets[i])) > 0) {
                                let thisQuestion = availableSheets[i],
                                    tqTop = thisQuestion.getBoundingClientRect().top,
                                    tqHeight = thisQuestion.getBoundingClientRect().height,
                                    nextQuestion = availableSheets[i + 1],
                                    nqTop = nextQuestion.getBoundingClientRect().top,
                                    nqHeight = nextQuestion.getBoundingClientRect().height;

                                /*if (visibleInQuestionList($(thisQuestion)) < thisQuestion.getBoundingClientRect().height) {
                                    scrolled = scrolled + tqHeight - visibleInQuestionList($(thisQuestion));
                                    qList.animate({scrollTop: scrolled},'slow');

                                } else {*/
                                scrolled = scrolled + (nqTop - qWrapperBottom + nqHeight + (visibleInQuestionList($(lastQuestion)) && lastQuestionGroup.length ? lastQuestionGroup.get(0).getBoundingClientRect().height : 0));
                                qList.animate({scrollTop: scrolled}, 'slow');
                                //}
                                break scrolldown;
                            } else if (i === 0) {
                                qList.animate({scrollTop: lastQuestionGroup.get(0).getBoundingClientRect().height}, 'slow');
                            }
                        }
                };

                // show me the buttons
                scope.$watch(
                    function () {
                        return getQuestionListWrapperHeight();
                    },
                    function (newValue, oldValue) {
                        if (oldValue == newValue) {
                            return;
                        }
                        showButtons();
                    }
                );

                $window.onresize = function () {
                    scope.$apply(function () {
                        showButtons();
                    });
                };

            }
        };
    }]);
}